"use client";
// app/components/tracking/TrackingScripts.tsx
// Settings'ten gelen pixel ID'leri ile koşullu script yükleme.
// Consent Mode: GA/GTM her zaman yüklenir (denied modda hit atmaz, modelling yapar).
// FB Pixel + TikTok: marketing consent verilince yüklenir.
import { useEffect, useRef } from "react";
import Script from "next/script";
import {
  getStoredConsent,
  CONSENT_CHANGED_EVENT,
} from "@/app/lib/tracking/consent";
import type { ConsentCategories } from "@/app/lib/tracking/types";

interface TrackingScriptsProps {
  ga4Id?: string;
  gtmId?: string;
  googleAdsId?: string;
  fbPixelId?: string;
  tiktokPixelId?: string;
}

// FB Pixel'i dinamik olarak DOM'a ekle (consent sonrası)
const loadFbPixel = (pixelId: string) => {
  if (typeof window === "undefined" || typeof window.fbq === "function") return;
  const script = document.createElement("script");
  script.innerHTML = `
    !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
    n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}
    (window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
    fbq('init','${pixelId}');fbq('track','PageView');
  `;
  document.head.appendChild(script);
};

// TikTok Pixel'i dinamik olarak DOM'a ekle (consent sonrası)
const loadTikTokPixel = (pixelId: string) => {
  if (typeof window === "undefined" || window.ttq) return;
  const script = document.createElement("script");
  script.innerHTML = `
    !function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];
    ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"];
    ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};
    for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);
    ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e};
    ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";
    ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};
    var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;
    var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};
    ttq.load('${pixelId}');ttq.page();}(window,document,'ttq');
  `;
  document.head.appendChild(script);
};

// GTM burada YÜKLENMİYOR — container'ın kendisi app/layout.tsx'te <head> içinde,
// consent script'inin hemen ardından senkron basılıyor.
//
// Eskiden burada bir `injectGtm` + ilk-etkileşim/3500 ms timeout yükleyicisi
// vardı. TBT'yi düşürüyordu ama container 3,5 saniye geç başlayınca GTM'in
// Initialization tetikleyicisindeki Google tag de o kadar geç başlıyor, hızlı
// çıkan ziyaretçilerde page_view hiç atılmıyordu. Ölçüm doğruluğu performansın
// önüne alındı; gerekçenin tamamı app/lib/tracking/gtm-script.ts'te.
//
// Bu bileşende artık yalnızca CONSENT'E BAĞLI pixel'ler var (FB, TikTok) ve
// GTM'in hiç tanımlı olmadığı standalone gtag modu.

const TrackingScripts = ({
  ga4Id,
  gtmId,
  googleAdsId,
  fbPixelId,
  tiktokPixelId,
}: TrackingScriptsProps) => {
  const marketingLoaded = useRef(false);

  useEffect(() => {
    const handleConsent = (categories: ConsentCategories | null) => {
      if (!categories?.marketing || marketingLoaded.current) return;
      marketingLoaded.current = true;
      if (fbPixelId) loadFbPixel(fbPixelId);
      if (tiktokPixelId) loadTikTokPixel(tiktokPixelId);
    };

    // Mevcut consent'i kontrol et
    const stored = getStoredConsent();
    handleConsent(stored);

    // Consent değişikliklerini dinle
    const listener = (e: Event) => {
      handleConsent((e as CustomEvent<ConsentCategories | null>).detail);
    };
    window.addEventListener(CONSENT_CHANGED_EVENT, listener);
    return () => window.removeEventListener(CONSENT_CHANGED_EVENT, listener);
  }, [fbPixelId, tiktokPixelId]);

  // GTM varsa, GA4 ve Ads konfigürasyonunu GTM üzerinden yönet.
  // gtag.js'yi sadece GTM yokken (standalone mod) yükle → ~280 KiB tasarruf.
  const standaloneGtag = !gtmId && (ga4Id || googleAdsId);

  return (
    <>
      {/* gtag.js — sadece GTM yoksa yükle (standalone mod) */}
      {standaloneGtag && (
        <>
          <Script
            src={`https://www.googletagmanager.com/gtag/js?id=${ga4Id || googleAdsId}`}
            strategy="afterInteractive"
          />
          <Script id="gtag-base" strategy="afterInteractive">{`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
          `}</Script>
        </>
      )}

      {/* GA4 config — GTM yoksa direkt yükle */}
      {standaloneGtag && ga4Id && (
        <Script id="ga4-config" strategy="afterInteractive">{`
          gtag('config', '${ga4Id}');
        `}</Script>
      )}

      {/* Google Ads config — GTM yoksa direkt yükle */}
      {standaloneGtag && googleAdsId && (
        <Script id="google-ads-config" strategy="afterInteractive">{`
          gtag('config', '${googleAdsId}');
        `}</Script>
      )}

      {/* GTM container + noscript iframe app/layout.tsx'te — burada hiçbir şey yok. */}
      {/* Microsoft Clarity — layout.tsx <head> içinde yükleniyor */}
    </>
  );
};

export default TrackingScripts;